import { DataGridProProps } from "@mui/x-data-grid-pro";
import { BudgetTableRow } from "../types";
import { useCurrentProject } from "@modules/projects/utils/useCurrentProject";
import { useBudgetTableActions } from "./useBudgetTableActions";
import { useEditExpenseMutation } from "gql/index";
import { useQuoteMutations } from "./useQuoteMutations";


interface Props {
  removeDraftBudgetRow: (draftId: string) => void;
  removeDraftCostRow: (budgetId: number, draftId: string) => void;
}

export const useBudgetRowUpdater = ({ removeDraftBudgetRow, removeDraftCostRow }: Props) => {
  const { projectId } = useCurrentProject();
  const { addBudgetItem, addCostItem, editBudgetItem, editCostItem } = useBudgetTableActions();

  const editExpense = useEditExpenseMutation();
  const { editQuote } = useQuoteMutations();
  
  const processRowUpdate: DataGridProProps<BudgetTableRow>['processRowUpdate'] = (newRow, oldRow) => {
    const isDraftRow = newRow.originalId === -1;
    const rawPlanned = newRow.planned;
    const plannedValue = (rawPlanned == null || Number.isNaN(Number(rawPlanned)))
      ? 0
      : Number(rawPlanned);

    if (newRow.name.trim() === '' || plannedValue < 0) {
      if (isDraftRow) {
        if (newRow.type === 'budget') {
          removeDraftBudgetRow(newRow.id);
        } else if (newRow.type === 'cost') {
          removeDraftCostRow(newRow.budgetItemId, newRow.id);
        }
      }
      return oldRow;
    }

    if (newRow.type === 'budget') {
      if (isDraftRow) {
        addBudgetItem({
          input: {
            projectId,
            name: newRow.name,
            budgetedAmount: plannedValue,
          }
        });
        removeDraftBudgetRow(newRow.id);
        return newRow;
      }
      editBudgetItem({
        input: {
          projectId,
          id: newRow.originalId,
          name: newRow.name,
          budgetedAmount: plannedValue
        }
      });
    } else if (newRow.type === 'cost') {
      if (isDraftRow) {
        addCostItem({
          input: {
            projectId,
            budgetItemId: newRow.budgetItemId,
            name: newRow.name,
            plannedAmount: plannedValue,
          }
        });
        removeDraftCostRow(newRow.budgetItemId, newRow.id);
        return newRow;
      }
      editCostItem({
        input: {
          id: newRow.originalId,
          projectId,
          name: newRow.name,
          plannedAmount: plannedValue,
          budgetItemId: newRow.budgetItemId,
          status: newRow.status,
        }
      });
    } else if (newRow.type === 'quote' && !isDraftRow) {
      editQuote.mutate({
        input: {
          id: newRow.originalId,
          projectId,
          name: newRow.name,
          amount: Number(newRow.quote.amount),
          quoteDate: newRow.quote.quoteDate,
          costItemId: newRow.costItemId,
        }
      });
    } else if (newRow.type === 'expense' && !isDraftRow) {
      editExpense.mutate({
        input: {
          costItemId: newRow.costItemId,
          expendedAmount: Number(newRow.expense.expendedAmount),
          expenseDate: newRow.expense.expenseDate,
          status: newRow.status,
          id: newRow.originalId,
          projectId,
          quoteId: newRow.quoteId,
          name: newRow.name,  
        }
      });
    }

    return newRow;
  };

  return {
    processRowUpdate,
  };
};